import {
  IconBox,
  IconChevronDown,
  IconChevronRight,
  IconCircleDot,
  IconHelpCircle,
  IconLayersIntersect,
  IconPackage,
  IconSettings,
  IconTool,
} from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { categories, type SubTypeOption } from "./data";
import { categoryLabel, subTypeLabel } from "./labels";

interface CategorySidebarProps {
  selectedCategory: string;
  selectedSubType: string | null;
  counts?: Record<string, number>;
  subTypes?: Record<string, SubTypeOption[]>;
  onSelectCategory: (id: string) => void;
  onSelectSubType: (type: string | null) => void;
}

const categoryIcons: Record<string, JSX.Element> = {
  box: <IconBox size={16} />,
  beam: <IconLayersIntersect size={16} />,
  cog: <IconSettings size={16} />,
  screw: <IconCircleDot size={16} />,
  package: <IconPackage size={16} />,
  wrench: <IconTool size={16} />,
  help: <IconHelpCircle size={16} />,
};

function CategorySidebar({
  selectedCategory,
  selectedSubType,
  counts,
  subTypes,
  onSelectCategory,
  onSelectSubType,
}: CategorySidebarProps) {
  const { t } = useTranslation();
  const [expandedCategories, setExpandedCategories] = useState<
    Record<string, boolean>
  >({ steel: true, screw: true });

  const toggleExpanded = (categoryId: string) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  const handleCategoryClick = (categoryId: string) => {
    onSelectCategory(categoryId);
    if (categoryId !== "steel" && categoryId !== "screw") {
      onSelectSubType(null);
    }
  };

  const handleSubTypeClick = (categoryId: string, typeId: string) => {
    onSelectCategory(categoryId);
    onSelectSubType(typeId);
  };

  return (
    <aside className="flex w-56 flex-col border-r border-gray-200 bg-white">
      <nav className="flex-1 space-y-1 overflow-auto px-2 pt-2">
        {categories.map((category) => {
          const isActive =
            selectedCategory === category.id && !selectedSubType;
          return (
            <div key={category.id}>
              <button
                onClick={() => {
                  handleCategoryClick(category.id);
                  if (category.subCategories) {
                    toggleExpanded(category.id);
                  }
                }}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-blue-50 font-medium text-blue-600"
                    : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`}
              >
                <span
                  className={`flex-shrink-0 ${
                    isActive ? "text-blue-600" : "text-gray-500"
                  }`}
                >
                  {categoryIcons[category.icon]}
                </span>
                <span className="flex-1 text-left">
                  {categoryLabel(t, category.id, category.name)}
                </span>
                {category.subCategories && (
                  <span className="text-gray-500">
                    {expandedCategories[category.id] ? (
                      <IconChevronDown size={16} />
                    ) : (
                      <IconChevronRight size={16} />
                    )}
                  </span>
                )}
                <span
                  className={`text-xs ${
                    isActive ? "text-blue-600" : "text-gray-500"
                  }`}
                >
                  {counts ? (counts[category.id] ?? 0) : category.count}
                </span>
              </button>

              {category.subCategories && expandedCategories[category.id] && (
                <div className="ml-4 mt-1 space-y-0.5">
                  {(subTypes?.[category.id] ?? []).length === 0 ? (
                    <p className="px-3 py-2 text-xs text-gray-400">
                      {t("itemEditor.sidebar.noData")}
                    </p>
                  ) : (
                    (subTypes?.[category.id] ?? []).map((sub) => (
                      <button
                        key={sub.id}
                        onClick={() => handleSubTypeClick(category.id, sub.id)}
                        className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                          selectedSubType === sub.id
                            ? "bg-blue-50 font-medium text-blue-600"
                            : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                        }`}
                      >
                        {sub.id !== sub.name && (
                          <span className="w-10 flex-shrink-0 font-mono text-xs text-gray-500">
                            {sub.id.length > 6 ? sub.id.slice(0, 6) : sub.id}
                          </span>
                        )}
                        <span className="flex-1 text-left">
                          {subTypeLabel(t, category.id, sub.id, sub.name)}
                        </span>
                        <span className="text-xs text-gray-500">{sub.count}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
}

export default CategorySidebar;
